import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { logger } from '@/lib/logger'

import { useAppStore } from '@/stores/app'
import { useAuthStore } from '@/stores/auth'
import { useLanguageStore } from '@/stores/language'
import { useAppSettings } from '@/stores/useAppSettings'
import { useBranchStore } from '@/stores/useBranchStore'

import LoadingSpinner from '@/components/LoadingSpinner'

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, fetchUser } = useAuthStore()
  const { fetchSettings } = useAppSettings()
  const setAppReady = useAppStore((s) => s.setAppReady)
  const isAppReady = useAppStore((s) => s.isAppReady)
  const fetchBranches = useBranchStore((s) => s.fetchBranches)
  // Ref to track if bootstrap has been called once already
  const hasBootstrapped = useRef(false)
  const { i18n } = useTranslation()

  useEffect(() => {
    if (hasBootstrapped.current) return
    hasBootstrapped.current = true

    const bootstrap = async () => {
      setAppReady(false)
      try {
        // Fetch settings with appropriate endpoint based on auth status
        const isAuthenticated = !!user

        // Fetch app settings first (public or authenticated based on user state)
        await fetchSettings(isAuthenticated)

        // If authenticated, fetch user data and branches
        // This will also trigger fresh app settings fetch in the auth store
        if (user) {
          await Promise.all([fetchUser(), fetchBranches()])
        }

        const freshUser = useAuthStore.getState().user
        const settings = useAppSettings.getState().settings
        const languageStore = useLanguageStore.getState()

        const preferredLang = languageStore.initializeLanguage(freshUser?.locale || settings?.default_locale)

        await i18n.changeLanguage(preferredLang)
        document.documentElement.dir = i18n.dir(preferredLang)
      } catch (error: unknown) {
        logger.error('App Bootstrap error', error)
      } finally {
        setAppReady(true)
      }
    }

    bootstrap()
  }, [])

  if (!isAppReady) {
    return <LoadingSpinner />
  }
  return <>{children}</>
}
